import { Drawer } from "@tvg/fuse/drawer";
import { useFocusTrap } from "client/components/utils/useFocusTrap";
import { useMainState } from "client/state/Main";
import React from "react";
import noScroll from "no-scroll";
import { Brand } from "types/common";
import { NavTreeDep } from "../../../../services/navigation/types";
import { AccountIcon, HomeIcon, MyOffersIcon } from "../icons";
import MenuDepartmentLink from "../menu-department-link/MenuDepartmentLink";
import MenuIconLink from "../menu-icon-link/MenuIconLink";
import { departmentHasSections } from "../navigation-bar/helpers/helpers";
import SlideOutDepartmentMenu from "../slideout-department-menu/SlideOutDepartmentMenu";
import styles from "./SlideOutMenu.module.css";

const { useRef, useState } = React;
export interface SlideOutMenuProps {
  brand: "very" | "veryireland" | "littlewoods";
  isOpen: boolean;
  closeButtonClick: () => void;
  dataTestId?: string;
  isCreditCustomer?: boolean;
}

const SlideOutMenu: React.FC<SlideOutMenuProps> = ({
  brand,
  dataTestId,
  closeButtonClick,
  isCreditCustomer,
  isOpen,
}) => {
  // Set default sub department active state to null
  const subDeptDefault: NavTreeDep = {
    name: "",
    link: "",
    image: "",
    splits: [],
  };
  // Department data
  const { navigation } = useMainState();
  const isMyOffersEnabled = useMainState((state) => state.isMyOffersEnabled);
  const myOffersHref = isMyOffersEnabled ? "/my-offers" : "/myoffers.page";
  // State
  const [activeSubDept, setActiveSubDept] =
    useState<NavTreeDep>(subDeptDefault);
  const containerRef = useRef<HTMLDivElement>(null);
  // Flag for active sub department with section. This is used to render 2nd level sub dept slideout
  const showDeptSlideOut = departmentHasSections(
    activeSubDept,
    activeSubDept.splits,
  );
  // Focus trap for main 1st level slide out. If 2nd level sub dept slideout is active, focus is cancelled
  useFocusTrap({
    isOpen,
    containerRef,
    onClose: () => {
      closeButtonClick();
      setActiveSubDept(subDeptDefault);
    },
    cancelFocus: showDeptSlideOut,
  });

  return (
    <Drawer
      className={`${styles.slideOutMenu} navi-header-fe-slide-out-menu`}
      title="Menu"
      closeCallback={() => {
        closeButtonClick();
        setActiveSubDept(subDeptDefault);
      }}
      dataTestId={dataTestId}
      desktopDisplay={false}
      open={isOpen}
      ref={containerRef}
      srLabelCloseButton="Close menu"
      zIndex="100"
    >
      {isOpen && (
        <nav className={`${styles.slideOutMenu__nav} slide-out-menu__nav`}>
          <ul
            className={`${styles.slideOutMenu__iconLinks} slide-out-menu__icon-link-ul`}
          >
            <li
              className={`${styles.slideOutMenu__iconLinkItem} slide-out-menu__icon-link-list-item`}
            >
              <MenuIconLink
                className="slide-out-menu__icon-link"
                href="/"
                icon={<HomeIcon />}
              >
                Home
              </MenuIconLink>
            </li>
            <li
              className={`${styles.slideOutMenu__iconLinkItem} slide-out-menu__icon-link-list-item`}
            >
              <MenuIconLink
                className="slide-out-menu__icon-link"
                href="/account/myaccount/accountSummary.page"
                icon={<AccountIcon />}
              >
                Account
              </MenuIconLink>
            </li>
            {brand === Brand.Very && isCreditCustomer && (
              <li
                className={`${styles.slideOutMenu__iconLinkItem} slide-out-menu__icon-link-list-item`}
              >
                <MenuIconLink
                  className="slide-out-menu__icon-link"
                  href={myOffersHref}
                  icon={<MyOffersIcon />}
                >
                  My Offers
                </MenuIconLink>
              </li>
            )}
          </ul>
          <div
            className={`${styles.slideOutMenu__contentBannerWrapper} slide-out-menu__content-banner-wrapper`}
          />
          <h3
            className={`${styles.slideOutMenu__departmentHeading} slide-out-menu__department-heading`}
          >
            Departments
          </h3>
          <ul
            className={`${styles.slideOutMenu__departmentLinks} slide-out-menu__department-links-ul`}
          >
            {navigation?.departments &&
              navigation.departments.length > 0 &&
              navigation.departments.map((dept) => {
                const deptHasSections = departmentHasSections(
                  dept,
                  dept.splits,
                );
                const deptKey = dept.name.replace(/[& ]/g, "-").toLowerCase();
                const anchorLink = deptHasSections ? `#${deptKey}` : dept.link;

                const handleClick = (e: React.MouseEvent) => {
                  deptHasSections && e.preventDefault();
                  if (deptHasSections) setActiveSubDept(dept);
                };

                const handleKeyDown = (e: React.KeyboardEvent) => {
                  if (deptHasSections && e.key === " ") setActiveSubDept(dept);
                };

                return (
                  <li
                    className={`${styles.slideOutMenu__departmentLinkItem} slide-out-menu__department-link-item`}
                    key={deptKey}
                  >
                    <MenuDepartmentLink
                      className={`${styles.slideOutMenu__departmentLink} slide-out-menu__department-link`}
                      href={anchorLink}
                      onClick={(e) => {
                        handleClick(e);
                      }}
                      onKeyDown={(e: React.KeyboardEvent) => handleKeyDown(e)}
                      role={deptHasSections ? "button" : undefined}
                    >
                      {dept.name}
                    </MenuDepartmentLink>
                  </li>
                );
              })}
          </ul>

          {/* Render 2nd level slideout for sub department with sections */}
          <SlideOutDepartmentMenu
            backButtonCallback={() => {
              setActiveSubDept(subDeptDefault);
              // Fixes issue when using 2 drawers conflicting when switching off no-scroll
              setTimeout(() => {
                noScroll.on();
              }, 0);
            }}
            closeButtonCallback={() => {
              closeButtonClick();
              setActiveSubDept(subDeptDefault);
            }}
            isOpen={showDeptSlideOut}
            subDepartments={activeSubDept.splits}
            title={activeSubDept.name}
            dataTestId={`slide-out-department-menu-${activeSubDept.name}`}
          />
        </nav>
      )}
    </Drawer>
  );
};

export default SlideOutMenu;
